<template>
  <div class="Step3HealthConditionsForm">
    <h2 class="gl_form-title small">
      {{ t(`hiring.common.health-conditions-form.legend.${person}`) }}
    </h2>

    <div
      v-for="(question, key) in healthSituation"
      :key="key"
      class="question"
    >
      <FormField
        :statement="
          te(`hiring.common.health-conditions-form.${key}.title.${person}`)
            ? t(`hiring.common.health-conditions-form.${key}.title.${person}`)
            : t(`hiring.common.health-conditions-form.${key}.title`)
        "
        tag="h3"
      >
        <CardInfoComponent v-if="te(`hiring.common.health-conditions-form.${key}.info`)">
          {{ t(`hiring.common.health-conditions-form.${key}.info`) }}
        </CardInfoComponent>
        <CardInfoComponent v-if="te(`hiring.common.health-conditions-form.${key}.info.${person}`)">
          {{ t(`hiring.common.health-conditions-form.${key}.info.${person}`) }}
        </CardInfoComponent>
        <YesNoComponent
          :id="`medical-condition-${key}`"
          :rules="[yesNoRequired]"
          v-model="question.checked"
        />
      </FormField>

      <template v-if="question.checked">
        <CheckButtonGroupComponent
          legend="Selecciona los asegurados"
          :id="`medical-conditions-insured-${key}`"
          :modelValue="question.insured"
          @update:modelValue="setInsured(key, $event)"
          :options="insuredOptions"
          :rules="[required]"
        />

        <template
          v-for="insuredIndex in question.insured"
          :key="String(insuredIndex)"
        >
          <UserCard
            class="user-card"
            :title="insuredOptions[Number(insuredIndex)].label"
          />
          <FormField
            v-if="
              isHealthSituationDescription(
                question.values[Number(insuredIndex)] as RenderValuesType
              )
            "
            :statement="
              te(`hiring.common.health-conditions-form.${key}.condition.me`) ||
              te(`hiring.common.health-conditions-form.${key}.condition.not-me`)
                ? props.answers.insureds[insuredIndex].role === 'TA'
                  ? t(`hiring.common.health-conditions-form.${key}.condition.me`)
                  : props.answers.insureds[insuredIndex].role === 'A'
                  ? t(`hiring.common.health-conditions-form.${key}.condition.not-me`)
                  : t(`hiring.common.health-conditions-form.${key}.condition.me`)
                : t(`hiring.common.health-conditions-form.${key}.condition`)
            "
          >
            <BaseText
              :id="`insured-${insuredIndex}-${key}`"
              :externalLabel="
                te(`hiring.common.health-conditions-form.${key}.condition.me`) ||
                te(`hiring.common.health-conditions-form.${key}.condition.not-me`)
                  ? props.answers.insureds[insuredIndex].role === 'TA'
                    ? t(`hiring.common.health-conditions-form.${key}.condition.me`)
                    : props.answers.insureds[insuredIndex].role === 'A'
                    ? t(`hiring.common.health-conditions-form.${key}.condition.not-me`)
                    : t(`hiring.common.health-conditions-form.${key}.condition.me`)
                  : t(`hiring.common.health-conditions-form.${key}.condition`)
              "
              rows="4"
              :placeholder="t(`hiring.common.health-conditions-form.${key}.condition-placeholder`)"
              v-model="
                (question.values[Number(insuredIndex)] as HealthSituationDescription).description
              "
              :max-characters="900"
              :rules="[required]"
            />
          </FormField>

          <FormField
            v-else
            :statement="t(`hiring.common.health-conditions-form.${key}.select`)"
          >
            <RadioButtonGroupComponent
              :id="`${key}-type-${insuredIndex}`"
              :options="getOptions(key, 'type')"
              :value="
                getDefaultOption(
                  (question.values[Number(insuredIndex)] as HealthSituationUsage).type,
                  key,
                  'type'
                )
              "
              v-model="(question.values[Number(insuredIndex)] as HealthSituationUsage).type"
              flexDirection="row"
              flexWrap="wrap"
              :rules="[validationRules.radioRequired]"
              :scroll="false"
            />
            <fieldset>
              <legend>{{ t(`hiring.common.health-conditions-form.${key}.error-amount`) }}</legend>

              <RadioButtonGroupComponent
                :id="`${key}-amount-${insuredIndex}`"
                :value="
                  getDefaultOption(
                    (question.values[Number(insuredIndex)] as HealthSituationUsage).frequency,
                    key,
                    'amount'
                  )
                "
                :options="getOptions(key, 'amount')"
                v-model="(question.values[Number(insuredIndex)] as HealthSituationUsage).frequency"
                :rules="[validationRules.radioRequired]"
                flexDirection="row"
                flexWrap="wrap"
                :scroll="false"
                :columns="2"
              />
            </fieldset>
          </FormField>
        </template>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import CardInfoComponent from '@/modules/common/common/components/panels/CardInfoComponent.vue'
  import YesNoComponent from '@/modules/core/components/formFields/YesNoComponent.vue'
  import BaseText from '@/modules/core/components/ui/BaseText.vue'
  import CheckButtonGroupComponent from '@/modules/core/components/ui/CheckButtonGroupComponent.vue'
  import RadioButtonGroupComponent from '@/modules/core/components/ui/RadioButtonGroupComponent.vue'
  import FormField from '@/modules/core/components/utils/FormField.vue'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import { QUESTION_OPTIONS } from '@/modules/core/constants/common'
  import { UserRoleEnum } from '@/modules/core/models/enums'
  import UserCard from '@/modules/health/hiring/components/UserCardForm.vue'
  import HealthHiringInsured from '@/modules/health/hiring/models/APP/HealthHiringInsured'
  import HealthHiringInsuredHealthSituation from '@/modules/health/hiring/models/APP/HealthHiringInsuredHealthSituation'
  import HealthSituationDescription from '@/modules/health/hiring/models/APP/HealthSituationDescription'
  import HealthSituationUsage from '@/modules/health/hiring/models/APP/HealthSituationUsage'
  import { format } from '@/utils/date.utils'
  import { computed, ref } from 'vue'
  import { useI18n } from 'vue-i18n'

  type RenderValuesType = HealthSituationDescription | HealthSituationUsage

  interface HealthSituationRenderData {
    checked: boolean | null
    insured: string[]
    values: RenderValuesType[]
  }

  const { t, te, locale } = useI18n()
  const { required, yesNoRequired } = useRules()

  defineExpose({ triggerSubmit })

  interface PropsInterface {
    nextForm: String[]
    answers: Record<string, any>
    configurations: Record<string, any>
  }
  const props = withDefaults(defineProps<PropsInterface>(), {})

  const validationRules = {
    radioRequired: (value: string) => required(value, 'common.validations.option')
  }

  const insuredOptions = ref(
    props.answers.insureds.map((item: HealthHiringInsured, i: number) => ({
      label: `${item.userDetails.name} (${
        item.userDetails.birthDate ? format(item.userDetails.birthDate) : ''
      })`,
      value: String(i)
    }))
  )
  const insureds = ref<HealthHiringInsured[]>(props.answers.insureds)

  const healthSituation = ref<Record<string, HealthSituationRenderData>>({
    cardiac: loadHealthSituation('cardiac'),
    metabolic: loadHealthSituation('metabolic'),
    rheumatic: loadHealthSituation('rheumatic'),
    nervous: loadHealthSituation('nervous'),
    hematologic: loadHealthSituation('hematologic'),
    urogenital: loadHealthSituation('urogenital'),
    psychiatric: loadHealthSituation('psychiatric'),
    infectious: loadHealthSituation('infectious'),
    medication: loadHealthSituation('medication'),
    alcohol: loadHealthSituation('alcohol'),
    smoking: loadHealthSituation('smoking'),
    drugs: loadHealthSituation('drugs'),
    injuries: loadHealthSituation('injuries'),
    disability: loadHealthSituation('disability')
  })

  const person = computed(() => {
    const number = props.answers.insureds.length
    const role = props.answers.insureds[0].role
    if (number === 1 && role === UserRoleEnum.HOLDER_INSURED) return 'me'
    else if (number === 1 && role === UserRoleEnum.INSURED) return 'not-me'
    else if (number > 1 && role === UserRoleEnum.HOLDER_INSURED) return 'holder'
    else return 'not-holder'
  })

  function setInsured(key: keyof typeof healthSituation.value, newInsured: string[]) {
    const previous = healthSituation.value[key].insured
    healthSituation.value[key].insured = newInsured

    const removedInsured = previous.filter((i) => !newInsured.includes(i))
    if (removedInsured.length) {
      healthSituation.value[key].values[Number(removedInsured)].reset()
    }
  }

  function getOptions(key: string, subKey: string) {
    try {
      const allOptions = QUESTION_OPTIONS[locale.value as keyof typeof QUESTION_OPTIONS]
      const keyOptions = allOptions[key as keyof typeof allOptions]

      return keyOptions[subKey as keyof typeof keyOptions]
    } catch (e) {
      console.error(e)
      return []
    }
  }

  function getDefaultOption(value: string, key: string, subKey: string) {
    const allOptions = getOptions(key, subKey)

    return value || allOptions[0].value
  }

  function isHealthSituationDescription(item: RenderValuesType) {
    return item instanceof HealthSituationDescription
  }

  function loadHealthSituation(
    key: keyof HealthHiringInsuredHealthSituation
  ): HealthSituationRenderData {
    const filled = insureds.value.some((i) => i.healthSituation.filled) || null
    const conditions = insureds.value?.map((i) => i.healthSituation[key]) ?? []
    const insuredWithPreviousData: string[] = conditions
      .map((c, i) => ((c as RenderValuesType)?.isEmpty() ? '' : String(i)))
      .filter((c) => c !== '')

    return {
      checked: filled ? insuredWithPreviousData.length > 0 : filled,
      insured: insuredWithPreviousData,
      values: conditions as RenderValuesType[]
    }
  }

  function getValuesToSave(key: string, insuredIndex: number) {
    const { checked, values } = healthSituation.value[key as keyof typeof healthSituation.value]
    if (!checked) return null

    return values[insuredIndex]
  }

  function triggerSubmit() {
    const page = props.nextForm[0]

    const insuredData = insureds.value.map((item, i) => {
      const hS = item.healthSituation

      hS.cardiac = getValuesToSave('cardiac', i) as HealthSituationDescription
      hS.metabolic = getValuesToSave('metabolic', i) as HealthSituationDescription
      hS.rheumatic = getValuesToSave('rheumatic', i) as HealthSituationDescription
      hS.nervous = getValuesToSave('nervous', i) as HealthSituationDescription
      hS.hematologic = getValuesToSave('hematologic', i) as HealthSituationDescription
      hS.urogenital = getValuesToSave('urogenital', i) as HealthSituationDescription
      hS.psychiatric = getValuesToSave('psychiatric', i) as HealthSituationDescription
      hS.infectious = getValuesToSave('infectious', i) as HealthSituationDescription
      hS.medication = getValuesToSave('medication', i) as HealthSituationDescription
      hS.alcohol = getValuesToSave('alcohol', i) as HealthSituationUsage
      hS.smoking = getValuesToSave('smoking', i) as HealthSituationUsage
      hS.drugs = getValuesToSave('drugs', i) as HealthSituationDescription
      hS.injuries = getValuesToSave('injuries', i) as HealthSituationDescription
      hS.disability = getValuesToSave('disability', i) as HealthSituationDescription
      hS.filled = true

      return item
    })

    const answers = { insureds: insuredData }
    return { page, answers }
  }
</script>

<style lang="scss" scoped>
  .Step3HealthConditionsForm {
    // este fragmento es para modificar el componente RadioButtonGroupComponent de este formulario concretamente
    :deep(.RadioButtonGroupComponent[columns='2']) {
      .options {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: $gap-m;
      }

      .choice {
        /* Anular reglas de flex de otros modos para que el grid gobierne */
        max-width: $size-full;
        flex: initial;
        min-width: $size-full;
        max-height: $size-73;
      }
    }

    fieldset {
      border: none;

      legend {
        font-family: $font-family-primary;
        font-size: $font-size-m;
        font-style: normal;
        font-weight: $font-weight-400;
        line-height: $line-height-100p;
        color: $color-neutral-dark;
      }
    }

    .question + .question {
      :deep(.gl_heading.secondary) {
        margin-top: $spacing-96;
      }
    }
  }
</style>
